import moment from 'moment-timezone';
import _ from 'lodash';
import { MEDIA_TYPES } from './Task.model';

const CENTRAL_TZ = 'America/Chicago';

export const TASK_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  COMPLETE: 'complete',
};

export const DEFAULT_LOOKBACK = '1_month';

/**
 * Lookback is client-side on purpose: one GET /admin/tasks, then filter.
 * Active requests and drafts always show. A server-side window would return
 * "actives + drafts + historical within N" and refetch on every dropdown
 * change. Revisit if there are too many tasks and performance becomes an issue.
 */
export const LOOKBACK_OPTIONS = [
  { value: '1_week', label: '1 week', amount: 1, unit: 'week' },
  { value: '1_month', label: '1 month', amount: 1, unit: 'month' },
  { value: '3_months', label: '3 months', amount: 3, unit: 'months' },
  { value: '6_months', label: '6 months', amount: 6, unit: 'months' },
  { value: 'all_time', label: 'All time', amount: null, unit: null },
];

export function getTaskStatus(task, now = moment()) {
  if (!task?.launch_date) {
    return TASK_STATUS.DRAFT;
  }

  if (task.ended_at == null && moment(task.due_date).isAfter(moment(now))) {
    return TASK_STATUS.ACTIVE;
  }

  return TASK_STATUS.COMPLETE;
}

/**
 * Split tasks into status buckets and sort for display: active soonest-due
 * first, historical most-recently-passed first. The two directions are why
 * sorting lives here rather than in GET /admin/tasks — one ORDER BY cannot
 * serve both, and the client would re-sort anyway.
 */
export function bucketTasks(tasks = [], now = moment()) {
  const buckets = { active: [], historical: [], drafts: [] };

  tasks.forEach((task) => {
    const status = getTaskStatus(task, now);
    if (status === TASK_STATUS.DRAFT) {
      buckets.drafts.push(task);
    } else if (status === TASK_STATUS.ACTIVE) {
      buckets.active.push(task);
    } else {
      buckets.historical.push(task);
    }
  });

  return {
    active: sortByDueDate(buckets.active, 'asc'),
    historical: sortByDueDate(buckets.historical, 'desc'),
    drafts: sortByDueDate(buckets.drafts, 'asc'),
  };
}

export function filterHistoricalByLookback(
  historicalTasks = [],
  lookbackValue = DEFAULT_LOOKBACK,
  now = moment()
) {
  const option = LOOKBACK_OPTIONS.find(
    (lookback) => lookback.value === lookbackValue
  );
  if (!option?.amount) {
    return historicalTasks;
  }

  const cutoff = moment(now).subtract(option.amount, option.unit);
  return historicalTasks.filter((task) =>
    moment(task.due_date).isSameOrAfter(cutoff)
  );
}

export function getTasksForDisplay(
  tasks = [],
  lookbackValue = DEFAULT_LOOKBACK,
  now = moment()
) {
  const { active, historical, drafts } = bucketTasks(tasks, now);
  const filteredHistorical = filterHistoricalByLookback(
    historical,
    lookbackValue,
    now
  );

  return {
    drafts,
    listedTasks: [...active, ...filteredHistorical],
  };
}

export function getRequestSubtitleParts(task) {
  const submissionCount = task?.submission_count ?? 0;

  return {
    submissions: `${submissionCount} ${pluralize(
      submissionCount,
      'submission'
    )}`,
    liked: String(task?.liked_count ?? 0),
    due: formatDueLabel(task?.due_date),
  };
}

export function formatDraftSubtitle(task) {
  const items = task?.content_requests || [];
  const photoCount = items.filter(
    (item) => item.media_type === MEDIA_TYPES.PHOTO
  ).length;
  const videoCount = items.filter(
    (item) => item.media_type === MEDIA_TYPES.VIDEO
  ).length;

  const media = [
    photoCount && `${photoCount} ${pluralize(photoCount, 'photo')}`,
    videoCount && `${videoCount} ${pluralize(videoCount, 'video')}`,
  ]
    .filter(Boolean)
    .join(' & ');

  return [media, formatDueLabel(task?.due_date)].filter(Boolean).join(' · ');
}

function sortByDueDate(tasks, direction) {
  return _.orderBy(
    tasks,
    [(task) => moment(task.due_date).valueOf()],
    [direction]
  );
}

function formatDueLabel(dueDate) {
  if (!dueDate) {
    return '';
  }

  return `Due ${moment(dueDate).tz(CENTRAL_TZ).format('MMM D')}`;
}

function pluralize(count, singular) {
  return count === 1 ? singular : `${singular}s`;
}
